import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function UpgradeSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | success | failed | expired
  const attemptsRef = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let alive = true;

    async function poll() {
      if (!alive) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/subscription/checkout-status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {}
      if (attemptsRef.current < 8) setTimeout(poll, 2000);
      else setStatus("failed");
    }
    poll();
    return () => { alive = false; };
  }, [params, refresh]);

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center px-6 text-center">
      {status === "checking" && (
        <>
          <Loader2 size={40} className="animate-spin text-violet-400" />
          <h1 className="mt-6 font-display text-3xl text-white">Confirming payment…</h1>
          <p className="mt-2 text-neutral-500 text-sm">This should only take a moment.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 size={48} className="text-emerald-400" data-testid="upgrade-success-icon" />
          <h1 className="mt-6 font-display text-4xl text-white">Welcome to <em className="not-italic bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text text-transparent">Immersive+</em>.</h1>
          <p className="mt-3 text-neutral-400">Everything's unlocked. Go build something wild.</p>
          <button data-testid="upgrade-success-continue" onClick={() => nav("/discover", { replace: true })} className="btn-pill btn-primary mt-8 px-8 py-3">Continue</button>
        </>
      )}
      {(status === "failed" || status === "expired") && (
        <>
          <XCircle size={48} className="text-rose-400" />
          <h1 className="mt-6 font-display text-3xl text-white">Payment didn't complete.</h1>
          <p className="mt-2 text-neutral-500 text-sm">{status === "expired" ? "Session expired." : "We couldn't confirm your payment. No charge was made if the checkout didn't complete."}</p>
          <button data-testid="upgrade-fail-retry" onClick={() => nav("/upgrade", { replace: true })} className="btn-pill btn-primary mt-8 px-8 py-3">Try again</button>
        </>
      )}
    </div>
  );
}
